import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, GlobeIcon, PlayIcon } from 'lucide-react';
import type { SlideProps } from './SlideShell';
import { EsteveWordmark } from '../EsteveWordmark';
import { LivePrototype } from '../LivePrototype';
import { InsightsOverlay } from '../insights/InsightsOverlay';
import { SnakPlayerOverlay } from '../SnakPlayerOverlay';
import { conceptDisclaimer } from '../../data/caprelsa';
const EASE = [0.23, 1, 0.32, 1] as const;
export function SlideOpening({}: SlideProps) {
  const [playerOpen, setPlayerOpen] = useState(false);
  const closePlayer = useCallback(() => setPlayerOpen(false), []);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const closeInsights = useCallback(() => setInsightsOpen(false), []);
  return <div className="relative flex h-full w-full overflow-hidden bg-[var(--surface)]" data-id="element-356" data-mp="2nidps11laa42">
      <motion.div initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} className="absolute left-0 top-0 h-1 w-full origin-left bg-[var(--brand)]" data-id="element-357" data-mp="2jg0k1cmuec7a" />

      <div className="flex min-h-0 flex-1 items-stretch gap-[clamp(2rem,5vw,5rem)] px-[clamp(2rem,5vw,5rem)] pb-[clamp(3.5rem,5vw,4.5rem)] pt-[clamp(3.5rem,5.5vw,5rem)]" data-id="element-358" data-mp="38ljg7g9ny9m5">
        <div className="flex min-w-0 flex-[1.05] flex-col justify-center" data-id="element-359" data-mp="2yuf18gjzmorb">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="flex items-center gap-4" data-id="element-360" data-mp="3mkj3ajhj2gg7">
            <EsteveWordmark data-id="element-361" data-mp="3riudivbqisyy" />
            <span className="h-8 w-px bg-[var(--fg)]/15" aria-hidden="true" data-id="element-362" data-mp="3djup9os4wg3b" />
            <p className="text-[clamp(0.62rem,0.85vw,0.8rem)] font-bold uppercase tracking-[0.24em] text-[var(--fg)]" data-id="element-363" data-mp="3dlxck11wjv4c">
              × Snakker<sup className="text-[0.6em]" data-id="element-364" data-mp="224xlt9v7docf">™</sup> · Caprelsa<sup className="text-[0.6em]" data-id="element-365" data-mp="223w6uub2ghpf">®</sup> concept
            </p>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 22
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.7,
          ease: EASE
        }} className="mt-[clamp(1.5rem,3vw,2.75rem)] text-[clamp(2.3rem,5vw,5rem)] font-extrabold leading-[0.98] tracking-[-0.055em] text-[var(--fg)]" data-id="element-366" data-mp="2672cbzqcu4uu">
            A small idea for ESTEVE.
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.35,
          duration: 0.6,
          ease: EASE
        }} className="mt-[clamp(0.5rem,1vw,0.9rem)] text-[clamp(1.35rem,2.6vw,2.5rem)] font-bold leading-[1.08] tracking-[-0.035em] text-[var(--brand)]" data-id="element-367" data-mp="3u3a9ew4yf0nu">
            A new way to bring Caprelsa’s clinical story to life.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.55
        }} className="mt-[clamp(1rem,2vw,1.6rem)] max-w-xl text-[clamp(0.92rem,1.25vw,1.15rem)] font-medium leading-snug text-[var(--muted)]" data-id="element-368" data-mp="3b7af3eoi7l13">
            An unsolicited concept, built to show how complex scientific information can become a short, interactive HCP experience.
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.5
        }} className="mt-3 flex items-center gap-2 text-[clamp(0.78rem,1vw,0.92rem)] font-semibold text-[var(--fg)]" data-id="element-369" data-mp="1qoges7l6namf">
            <GlobeIcon className="h-4 w-4 text-[var(--brand)]" aria-hidden="true" data-id="element-370" data-mp="12yc7nq8scsqo" />
            Built once — reusable and localizable across ESTEVE markets.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7,
          duration: 0.5
        }} className="mt-[clamp(1.4rem,2.6vw,2.25rem)] flex flex-wrap items-center gap-[clamp(0.5rem,0.8vw,0.75rem)] hide-on-print" data-id="element-371" data-mp="1cw0gtl7522sd">
            <button type="button" onClick={() => setPlayerOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-[clamp(1.1rem,1.7vw,1.5rem)] py-[clamp(0.6rem,0.95vw,0.85rem)] text-[clamp(0.68rem,0.85vw,0.8rem)] font-bold uppercase tracking-[0.14em] text-white shadow-sm transition-[background-color,transform] duration-150 hover:bg-[var(--fg)] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-372" data-mp="13hmhp1cz7mt">
              <PlayIcon className="h-3.5 w-3.5 fill-current" aria-hidden="true" data-id="element-373" data-mp="stmea8rg6wy2" />
              Play the Snak
            </button>
            <button type="button" onClick={() => setInsightsOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-[var(--fg)]/15 bg-white px-[clamp(1.1rem,1.7vw,1.5rem)] py-[clamp(0.6rem,0.95vw,0.85rem)] text-[clamp(0.68rem,0.85vw,0.8rem)] font-bold uppercase tracking-[0.14em] text-[var(--fg)] transition-[border-color,transform] duration-150 hover:border-[var(--fg)]/40 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-374" data-mp="orsfk60tc30j">
              <BarChart3Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-375" data-mp="1cf5rwz8q2ai8" />
              See the insights
            </button>

          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15,
        duration: 0.65,
        ease: EASE
      }} className="hidden min-w-0 flex-1 md:flex" data-id="element-376" data-mp="ympymq5mhqui">
          <LivePrototype playKey={0} data-id="element-377" data-mp="toxwz4f6eqd4" />
        </motion.div>
      </div>

      <p className="absolute bottom-4 left-[clamp(2rem,5vw,5rem)] right-28 font-mono text-[clamp(0.52rem,0.68vw,0.64rem)] tracking-[0.1em] text-[var(--muted)]" data-id="element-378" data-mp="tmtsak9gdnbs">
        {conceptDisclaimer}
      </p>
      <InsightsOverlay open={insightsOpen} onClose={closeInsights} data-id="element-379" data-mp="jsut9u4oejo2" />
      <SnakPlayerOverlay open={playerOpen} onClose={closePlayer} data-id="element-380" data-mp="2me7866m5rhon" />
    </div>;
}